import { Toaster } from '@ixigo/iui/Toast';
import { useNetworkStatus } from '@repo/shared/hooks';
import { createFileRoute } from '@tanstack/react-router';
import { Fragment } from 'react';
import { DEFAULT_ERROR_DESCRIPTION, DEFAULT_ERROR_TITLE } from '@/api';
import {
	fetchFareDetails,
	type FareDetailsResponse as ReviewPageLoaderData,
} from '@/api/fareDetails';
import {
	FARE_DETAIL_ERROR_MAP,
	FareDetailView as FareDetailErrorView,
	NoInternet,
} from '@/components/error-states';
import { useNavigate } from '@/lib/navigation';
import { useAppStore } from '@/store';
import { ReviewPageContent } from './-components';
import { ReviewPageLoading } from './-components/page-loading';
import { getReviewPageData } from './-store/selectors';
import { parseReviewSearchParams } from './-utils';
import type { ReviewSearchParams } from './-utils/searchParams';

interface ReviewPageLoaderContext {
	searchParams: ReviewSearchParams | null;
}

/**
 * Loads review page data from store or API
 * @param context - TanStack Router loader context
 * @returns Promise with review page data or error
 */
async function reviewPageLoader(
	context: ReviewPageLoaderContext,
): Promise<ReviewPageLoaderData> {
	const store = useAppStore.getState();
	const { tripDetails, farePolicies, shouldForceRefresh } = store.reviewPage;

	if (tripDetails && farePolicies && !shouldForceRefresh) {
		const reviewPageData = getReviewPageData(store);
		if (reviewPageData) {
			return reviewPageData;
		}
	}

	const { searchParams } = context;
	if (!searchParams) {
		return {
			errors: {
				title: DEFAULT_ERROR_TITLE,
				message: DEFAULT_ERROR_DESCRIPTION,
				type: 'GENERIC_ERROR',
			},
		};
	}

	const res = await fetchFareDetails(searchParams);
	if ('data' in res) {
		store.reviewPageActions.setFareDetails(res.data);
		const selectedCab = res.data.tripDetails.cabSelection.options.find(
			(cab) => cab.cabId === searchParams.cabId,
		);
		if (selectedCab) {
			store.reviewPageActions.setSelectedCab(selectedCab);
		}
	}

	return res;
}

export const Route = createFileRoute('/review/')({
	validateSearch: (search: Record<string, unknown>) => {
		return search as {
			pickup?: string;
			destination?: string;
			dateTime?: string;
			cabId?: string;
		};
	},
	loader: (context) => {
		const urlSearchParams = context.location.search;
		const searchParams = parseReviewSearchParams(urlSearchParams);
		return reviewPageLoader({
			searchParams,
		});
	},
	component: ReviewPage,
	pendingComponent: () => {
		return <ReviewPageLoading />;
	},
	pendingMs: 300,
	pendingMinMs: 600,
});

function ReviewPage() {
	const reviewPageData = Route.useLoaderData();
	const { online } = useNetworkStatus();
	const navigate = useNavigate();
	const hasError = 'errors' in reviewPageData;

	if (!online) {
		return <NoInternet />;
	}

	if (hasError) {
		const { title, message, type } = reviewPageData.errors;
		return (
			<FareDetailErrorView
				title={title}
				description={message}
				errorType={type}
				errorMap={{
					NOT_SERVICEABLE: {
						...FARE_DETAIL_ERROR_MAP.NOT_SERVICEABLE,
						btnText: 'Go to Home',
					},
					AIRPORT_ONLY: {
						...FARE_DETAIL_ERROR_MAP.AIRPORT_ONLY,
						btnText: 'Go to Home',
					},
					NO_CABS: { ...FARE_DETAIL_ERROR_MAP.NO_CABS, btnText: 'Go to Home' },
					GENERIC_ERROR: {
						...FARE_DETAIL_ERROR_MAP.GENERIC_ERROR,
						btnText: 'Go to Home',
					},
				}}
				onAction={() => {
					navigate({ to: '/', back: true });
				}}
			/>
		);
	}

	return (
		<Fragment>
			<Toaster />
			<ReviewPageContent reviewPageData={reviewPageData.data} />
		</Fragment>
	);
}
